/**
 * FeatureSplitPanel - Entity-specific split panel for Features
 * 
 * Inverted merge pattern:
 * - Left: Single source feature (view-only, tabbed)
 * - Right: Multiple resulting features (expandable, editable)
 * 
 * This provides a clear visual flow: Source → Results
 * Users can see exactly what they're splitting and edit each result.
 * 
 * Props:
 * - originalFeature: The feature being split
 * - splitData: { feature_1: {...}, feature_2: {...} } - AI suggestions
 * - onFeature1Change: (field, value) => void
 * - onFeature2Change: (field, value) => void
 * - feature1Data: Current state of feature 1
 * - feature2Data: Current state of feature 2
 * - productId: string
 * - errors: { feature_1: {}, feature_2: {} } - validation errors
 * - linkableEntities: { epics, personas, tags, etc. } - for dropdowns
 */

import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  GitBranch,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import FeatureOverviewForm from '../feature/FeatureOverviewForm';
import FeatureRequirementsPanel from '../feature/FeatureRequirementsPanel';

// =============================================================================
// SOURCE FEATURE VIEW (Left Panel)
// =============================================================================

const SourceFeatureView = ({
  feature,
  colors,
  linkableEntities = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const requirementCount = feature?.requirements?.length || 0;
  const completedCount = (feature?.requirements || []).filter(r => 
    typeof r === 'object' ? r.is_completed : false
  ).length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 pb-3 border-b border-gray-200 dark:border-gray-700 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <div className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            "bg-gray-200 dark:bg-gray-700"
          )}>
            <Layers className="w-4 h-4 text-gray-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={cn("font-medium truncate", colors.textPrimary)}>
              {feature?.title || 'Untitled Feature'}
            </h4>
            <p className={cn("text-xs", colors.textMuted)}>
              Original feature being split
            </p>
          </div>
        </div>
        
        {/* Quick stats */}
        <div className="flex items-center gap-3 text-xs">
          <span className={cn("px-2 py-0.5 rounded", colors.bgTertiary, colors.textSecondary)}>
            {feature?.status || 'Backlog'}
          </span>
          {requirementCount > 0 && (
            <span className={colors.textMuted}>
              {completedCount}/{requirementCount} requirements complete
            </span>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="overview" className="text-xs">
              Overview
            </TabsTrigger>
            <TabsTrigger value="requirements" className="text-xs">
              Requirements ({requirementCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <FeatureOverviewForm
            formData={feature || {}}
            isEditing={false}
            isDisabled={true}
            colors={colors}
            mode="view"
            epics={linkableEntities.epics || []}
            releases={linkableEntities.releases || []}
            personas={linkableEntities.personas || []}
            availableTags={linkableEntities.tags || []}
            teamMembers={linkableEntities.teamMembers || []}
          />
        )}
        
        {activeTab === 'requirements' && (
          <FeatureRequirementsPanel
            requirements={feature?.requirements || []}
            isEditing={false}
            colors={colors}
            mode="view"
          />
        )}
      </div>
    </div>
  );
};

// =============================================================================
// RESULT FEATURE CARD (Right Panel - Expandable)
// =============================================================================

const ResultFeatureCard = ({
  feature,
  onChange,
  index,
  isExpanded,
  onToggleExpand,
  badgeColor,
  badgeLabel,
  linkableEntities,
  colors,
  errors = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const requirementCount = feature?.requirements?.length || 0;
  
  // Handle field changes
  const handleChange = (field, value) => {
    onChange(field, value);
  };
  
  // Requirement handlers
  const handleAddRequirement = (text) => {
    const newReq = {
      id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text,
      is_completed: false,
    };
    onChange('requirements', [...(feature.requirements || []), newReq]);
  };
  
  const handleUpdateRequirement = (reqId, field, value) => {
    const updated = (feature.requirements || []).map(req => 
      req.id === reqId ? { ...req, [field]: value } : req
    );
    onChange('requirements', updated);
  };
  
  const handleDeleteRequirement = (reqId) => {
    const updated = (feature.requirements || []).filter(req => req.id !== reqId);
    onChange('requirements', updated);
  };
  
  const handleToggleRequirementComplete = (reqId, isCompleted) => {
    const updated = (feature.requirements || []).map(req => 
      req.id === reqId ? { ...req, is_completed: isCompleted } : req
    );
    onChange('requirements', updated);
  };

  // Badge styling
  const badgeClasses = badgeColor === 'blue'
    ? 'bg-blue-500/20 text-blue-500 border-blue-500/30'
    : 'bg-purple-500/20 text-purple-500 border-purple-500/30';
  
  const borderClass = badgeColor === 'blue'
    ? 'border-blue-300 dark:border-blue-600'
    : 'border-purple-300 dark:border-purple-600';

  // Check for errors in overview
  const hasOverviewErrors = Object.keys(errors).some(k => ['title', 'description', 'user_story'].includes(k));

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isExpanded ? borderClass : "border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-800"
      )}
      data-testid={`result-feature-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Badge */}
        <Badge className={cn("text-xs", badgeClasses)}>
          {badgeLabel}
        </Badge>
        
        {/* Feature Title */}
        <div className="flex-1 min-w-0">
          <span className={cn(
            "font-medium truncate block",
            colors.textPrimary,
            !feature.title && "italic text-gray-400"
          )}>
            {feature.title || 'Untitled - Click to expand'}
          </span>
          {!isExpanded && requirementCount > 0 && (
            <span className={cn("text-xs", colors.textMuted)}>
              {requirementCount} requirement{requirementCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        {/* Error indicator */}
        {hasOverviewErrors && !isExpanded && (
          <Badge variant="destructive" className="text-xs">
            Missing required fields
          </Badge>
        )}
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger 
                  value="overview" 
                  className={cn("text-xs", hasOverviewErrors && "text-red-500")}
                >
                  Overview {hasOverviewErrors && '⚠️'}
                </TabsTrigger>
                <TabsTrigger value="requirements" className="text-xs">
                  Requirements ({requirementCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3 max-h-[400px] overflow-y-auto">
            {activeTab === 'overview' && (
              <FeatureOverviewForm
                formData={feature}
                onChange={handleChange}
                isEditing={true}
                isDisabled={false}
                colors={colors}
                epics={linkableEntities.epics || []}
                releases={linkableEntities.releases || []}
                personas={linkableEntities.personas || []}
                teamMembers={linkableEntities.teamMembers || []}
                availableTags={linkableEntities.tags || []}
                errors={errors}
                mode="create"
              />
            )}
            
            {activeTab === 'requirements' && (
              <FeatureRequirementsPanel
                requirements={feature.requirements || []}
                onAdd={handleAddRequirement}
                onUpdate={handleUpdateRequirement}
                onDelete={handleDeleteRequirement}
                onToggleComplete={handleToggleRequirementComplete}
                isEditing={true}
                colors={colors}
                mode="create"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SPLIT IMPACT SUMMARY
// =============================================================================

const SplitImpactSummary = ({ originalFeature, feature1, feature2, colors }) => {
  const originalReqCount = originalFeature?.requirements?.length || 0;
  const originalCompletedCount = (originalFeature?.requirements || []).filter(r => 
    typeof r === 'object' ? r.is_completed : false
  ).length;
  
  const feature1ReqCount = feature1?.requirements?.length || 0;
  const feature2ReqCount = feature2?.requirements?.length || 0;
  const totalNewReqCount = feature1ReqCount + feature2ReqCount;

  return (
    <div className={cn(
      "p-3 rounded-lg border",
      "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
    )}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Split Impact
        </span>
      </div>
      <div className="space-y-1 text-xs">
        <div className="flex items-center gap-2">
          <GitBranch className="w-3 h-3 text-amber-600" />
          <span className="text-amber-700 dark:text-amber-300">
            Original feature will be <strong>deleted</strong> after split
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ArrowRight className="w-3 h-3 text-amber-600" />
          <span className="text-amber-700 dark:text-amber-300">
            {originalReqCount} requirements → {feature1ReqCount} + {feature2ReqCount} = {totalNewReqCount}
          </span>
        </div>
        {originalCompletedCount > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 text-red-500" />
            <span className="text-red-600 dark:text-red-400">
              {originalCompletedCount} completed requirement{originalCompletedCount !== 1 ? 's' : ''} will need re-verification
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <span className="text-amber-700 dark:text-amber-300">
            Both new features will be placed in <strong>Backlog</strong> status
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const FeatureSplitPanel = ({
  originalFeature,
  feature1Data,
  feature2Data,
  onFeature1Change,
  onFeature2Change,
  linkableEntities = {},
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Expansion state - Feature 1 expanded by default
  const [expandedCards, setExpandedCards] = useState({ 0: true, 1: false });
  
  const toggleExpand = (index) => {
    setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Feature (View Only) */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Source Feature
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This feature will be split into two new features
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <SourceFeatureView
            feature={originalFeature}
            colors={colors}
            linkableEntities={linkableEntities}
          />
        </div>
      </div>
      
      {/* Right Panel: Resulting Features (Expandable) */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Resulting Features
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the new features before splitting
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Feature 1 */}
          <ResultFeatureCard
            feature={feature1Data}
            onChange={onFeature1Change}
            index={0}
            isExpanded={expandedCards[0]}
            onToggleExpand={() => toggleExpand(0)}
            badgeColor="blue"
            badgeLabel="Feature 1"
            linkableEntities={linkableEntities}
            colors={colors}
            errors={errors.feature_1 || {}}
          />
          
          {/* Feature 2 */}
          <ResultFeatureCard
            feature={feature2Data}
            onChange={onFeature2Change}
            index={1}
            isExpanded={expandedCards[1]}
            onToggleExpand={() => toggleExpand(1)}
            badgeColor="purple"
            badgeLabel="Feature 2"
            linkableEntities={linkableEntities}
            colors={colors}
            errors={errors.feature_2 || {}}
          />
          
          {/* Split Impact Summary */}
          <SplitImpactSummary
            originalFeature={originalFeature}
            feature1={feature1Data}
            feature2={feature2Data}
            colors={colors}
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureSplitPanel;
